import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  OnInit,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Observable, combineLatest } from 'rxjs';
import { takeUntil, map, startWith } from 'rxjs/operators';
import { RequestsFacade } from '../../store/requests/requests.facade';
import { Request } from '../../core/models/request.model';
import { CreateRequestModalService } from '../../core/services/create-request-modal.service';
import { RequestFilter } from '../../core/models/request-filter.model';
import {
  RequestStatus,
  RequestStatusLabels,
} from '../../core/enums/request-status.enum';
import { CreateRequestWizardComponent } from './create-request-wizard/create-request-wizard.component';
import { BulkUpdateModalComponent } from './bulk-update-modal/bulk-update-modal.component';
import { BaseTableComponent } from '../../shared/components/base-table/base-table.component';
import { FilterChipsBarComponent } from '../../shared/components/filter-chips-bar/filter-chips-bar.component';
import {
  ColumnVisibilityToggleComponent,
  GridColumn,
} from '../../shared/components/column-visibility-toggle/column-visibility-toggle.component';
import {
  UkgTableData,
  ColumnData,
  transformToTableData,
  nullishFallback,
} from '../../shared/utils/table-data-transformer';
import { RequestFilterMapper } from '../../shared/utils/request-filter-mapper';
import { FilterPersistenceService } from '../../shared/services/filter-persistence.service';
import { FilterPersistenceManager } from '../../shared/utils/filter-persistence-manager';
import { HasPermissionDirective } from '../../shared/directives/has-permission.directive';
import { Permissions } from '../../core/enums';
import {
  resetColumnFilters,
  getActiveFilterCount,
  hasActiveFilters,
} from '../../shared/utils/filter-reset.utility';
import { LoadingStateComponent } from '../../shared/components/loading-state/loading-state.component';
import { ErrorStateComponent } from '../../shared/components/error-state/error-state.component';
import { ColumnSearchRowComponent } from '../../shared/components/column-search-row/column-search-row.component';
import { ResultsInfoComponent } from '../../shared/components/results-info/results-info.component';
import { FilterPanelWrapperComponent } from '../../shared/components/filter-panel-wrapper/filter-panel-wrapper.component';
import { SearchInputComponent } from '../../shared/components/search-input/search-input.component';
import { PaginationComponent } from '../../shared/components/pagination/pagination.component';

@Component({
  selector: 'app-requests',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CreateRequestWizardComponent,
    BulkUpdateModalComponent,
    FilterChipsBarComponent,
    HasPermissionDirective,
    ColumnVisibilityToggleComponent,
    LoadingStateComponent,
    ErrorStateComponent,
    ColumnSearchRowComponent,
    ResultsInfoComponent,
    FilterPanelWrapperComponent,
    SearchInputComponent,
    PaginationComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './requests.component.html',
  styleUrls: ['./requests.component.scss'],
})
export class RequestsComponent extends BaseTableComponent implements OnInit {
  protected override readonly facade = inject(RequestsFacade);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly filterPersistence = inject(FilterPersistenceService);
  private readonly createRequestModalService = inject(
    CreateRequestModalService
  );
  private readonly filterManager = new FilterPersistenceManager(
    this.route,
    this.router,
    this.filterPersistence,
    RequestFilterMapper,
    'requestsFilters',
    '/requests'
  );

  // Expose Math to template
  override readonly Math = Math;

  // State from facade
  readonly requests$ = this.facade.requests$;
  readonly loading$ = this.facade.loading$;
  readonly error$ = this.facade.error$;
  readonly filterOptions$ = this.facade.filterOptions$;
  readonly currentFilter$ = this.facade.currentFilter$;
  readonly Permissions = Permissions;

  showCreateWizard = false;
  showBulkUpdateModal = false;

  // Filters
  statusFilter: string | null = '';
  fromDateFilter: string | null = this.getDefaultFromDate();
  toDateFilter: string | null = this.getDefaultToDate();

  readonly booleanOptions = [
    { value: true, label: 'Yes' },
    { value: false, label: 'No' },
  ];

  // Dynamic filter options from API
  availableStatuses: string[] = [];

  readonly statusColorMap: Record<string, string> = {
    Sent: 'status-completed',
    Completed: 'status-completed',
    Open: 'status-in-progress',
    'In Progress': 'status-in-progress',
    Pending: 'status-pending',
    New: 'status-pending',
    Received: 'status-pending',
    Cancelled: 'status-cancelled',
    'On Hold': 'status-on-hold',
    Error: 'status-error',
    Failed: 'status-error',
  };

  selectedRows = new Set<string>();
  selectAll = false;
  selectedTableRows: UkgTableData[] = [];
  selectedRequests: Request[] = [];

  readonly columns: GridColumn[] = [
    {
      field: 'requestNumber',
      header: 'Request ID',
      width: 'auto',
      locked: true,
    },
    { field: 'status', header: 'Status', width: 'auto', type: 'status' },
    {
      field: 'requestDate',
      header: 'Date of Request',
      width: 'auto',
      type: 'date',
    },
    { field: 'arNumber', header: 'AR No', width: 'auto' },
    { field: 'country', header: 'Country', width: 'auto' },
    { field: 'printSite', header: 'Print Site', width: 'auto' },
    { field: 'createdBy', header: 'Created By', width: 'auto' },
    { field: 'assignedTo', header: 'Assigned To', width: 'auto' },
    {
      field: 'distributionCount',
      header: 'Distro Count',
      width: 'auto',
      type: 'number',
    },
    {
      field: 'documentCount',
      header: 'Doc Count',
      width: 'auto',
      type: 'number',
    },
    {
      field: 'isExpedited',
      header: 'Expedited?',
      width: 'auto',
      type: 'boolean',
      sortable: false,
    },
    {
      field: 'isPrinted',
      header: 'Printed?',
      width: 'auto',
      type: 'boolean',
      sortable: false,
    },
    {
      field: 'shippingMethods',
      header: 'Shipping Method',
      width: 'auto',
      sortable: false,
    },
    {
      field: 'requiresManualCheck',
      header: 'Manual Check?',
      width: 'auto',
      type: 'boolean',
      sortable: false,
    },
  ].map(col => ({
    ...col,
    sortable: col.sortable ?? true,
    visible: true,
  })) as GridColumn[];

  readonly columnWidthMap: Record<string, string> = {
    requestNumber: '120px',
    status: '120px',
    requestDate: '140px',
    arNumber: '120px',
    country: '140px',
    printSite: '100px',
    createdBy: '180px',
    assignedTo: '150px',
    distributionCount: '110px',
    documentCount: '100px',
    isExpedited: '100px',
    isPrinted: '90px',
    shippingMethods: '100px',
    requiresManualCheck: '100px',
  };

  override currentSortField: string | null = null;
  override currentSortDirection: 'asc' | 'desc' | null = null;

  // Column visibility
  protected readonly columnVisibilityKeyName = 'requestsColumnVisibility';
  readonly widthRatios: Record<string, number> = {
    requestNumber: 120,
    status: 120,
    requestDate: 140,
    arNumber: 120,
    country: 140,
    printSite: 100,
    createdBy: 180,
    assignedTo: 150,
    distributionCount: 110,
    documentCount: 100,
    isExpedited: 100,
    isPrinted: 90,
    shippingMethods: 100,
    requiresManualCheck: 100,
  };

  get visibleColumns(): GridColumn[] {
    return this.columnVisibilityService.getVisibleColumns(this.columns);
  }

  get filterRowGridTemplate(): string {
    const template = this.columnVisibilityService.getFilterRowGridTemplate(
      this.visibleColumns,
      this.widthRatios
    );
    return `40fr ${template}`;
  }

  override toggleColumnVisibility(): void {
    super.toggleColumnVisibility(this.columns);
  }

  override resetColumnVisibility(): void {
    super.resetColumnVisibility(this.columns);
  }

  protected override loadColumnVisibility(): void {
    super.loadColumnVisibility(this.columns);
  }

  // Ignite table properties
  columnNames: ColumnData = {
    requestNumber: { label: 'Request ID', sorted: 'none' },
    status: { label: 'Status', sorted: 'none' },
    requestDate: { label: 'Date of Request', sorted: 'none' },
    arNumber: { label: 'AR No', sorted: 'none' },
    country: { label: 'Country', sorted: 'none' },
    printSite: { label: 'Print Site', sorted: 'none' },
    createdBy: { label: 'Created By', sorted: 'none' },
    assignedTo: { label: 'Assigned To', sorted: 'none' },
    distributionCount: {
      label: 'Distro Count',
      sorted: 'none',
      align: 'right',
    },
    documentCount: { label: 'Doc Count', sorted: 'none', align: 'right' },
    isExpedited: { label: 'Expedited?', sorted: 'none' },
    isPrinted: { label: 'Printed?', sorted: 'none' },
    shippingMethods: { label: 'Shipping Method', sorted: 'none' },
    requiresManualCheck: { label: 'Manual Check?', sorted: 'none' },
  };

  // Transform requests data to Ignite table format
  tableData$: Observable<UkgTableData[]> = this.facade.requests$.pipe(
    map(pagedResult =>
      transformToTableData(pagedResult, request => ({
        requestNumber: request.requestNumber,
        status: nullishFallback(request.statusDisplay),
        requestDate: this.formatDate(request.requestDate),
        arNumber: nullishFallback(request.arNumber),
        country: nullishFallback(request.country),
        printSite: nullishFallback(request.printSite),
        createdBy: nullishFallback(request.createdBy),
        assignedTo: nullishFallback(request.assignedTo),
        distributionCount: request.distributionCount
          ? request.distributionCount.toString()
          : 'Pending',
        documentCount: request.documentCount?.toString() ?? '0',
        isExpedited: request.isExpedited ? 'Yes' : 'No',
        isPrinted: this.formatPrintedValue(request.isPrinted),
        shippingMethods: nullishFallback(request.shippingMethods),
        requiresManualCheck: request.requiresManualCheck ? 'Yes' : 'No',
      }))
    )
  );

  // Filtered table data with column filters applied
  filteredTableData$: Observable<UkgTableData[]> = combineLatest([
    this.tableData$,
    this.columnFiltersSubject
      .asObservable()
      .pipe(startWith(this.columnFilters)),
  ]).pipe(map(([data, filters]) => this.filterTableData(data, filters)));

  private getDefaultFromDate(): string {
    const today = new Date();
    const oneMonthAgo = new Date(
      today.getFullYear(),
      today.getMonth() - 1,
      today.getDate()
    );
    return oneMonthAgo.toISOString().split('T')[0];
  }

  private getDefaultToDate(): string {
    const today = new Date();
    return today.toISOString().split('T')[0];
  }

  ngOnInit(): void {
    // Load column visibility preferences
    this.loadColumnVisibility();

    // Restore filters from URL SYNCHRONOUSLY before initial load
    const queryParams = this.route.snapshot.queryParams;
    const restored = this.filterManager.restoreFilters(queryParams, {
      statusFilter: '',
      fromDateFilter: this.getDefaultFromDate(),
      toDateFilter: this.getDefaultToDate(),
      searchTerm: '',
    });

    this.statusFilter = restored.statusFilter ?? '';
    this.fromDateFilter = restored.fromDateFilter ?? this.fromDateFilter;
    this.toDateFilter = restored.toDateFilter ?? this.toDateFilter;
    this.searchTerm = restored.searchTerm ?? '';
    this.filterManager.setFiltersRestored(true);

    // Load filter options and requests with restored filters
    this.facade.loadFilterOptions();

    // Load requests with the restored filters (fromDate, toDate, etc.)
    this.applyFilters();

    // Subscribe to filter options for future updates
    this.filterOptions$.pipe(takeUntil(this.destroy$)).subscribe(options => {
      if (options) {
        this.availableStatuses = options.statuses ?? [];
      }
    });

    // Monitor query params for same-route navigation with guard
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      // Skip if params haven't changed (prevents infinite loop)
      if (!this.filterManager.hasParamsChanged(params)) return;

      this.filterManager.updateLastNavigatedParams(params);

      // If navigated without params, restore from localStorage
      if (this.filterManager.shouldRestoreFromLocalStorage(params)) {
        const restored = this.filterManager.restoreFromLocalStorage({
          statusFilter: '',
          fromDateFilter: this.fromDateFilter,
          toDateFilter: this.toDateFilter,
          searchTerm: '',
        });

        this.statusFilter = restored.statusFilter ?? '';
        this.fromDateFilter = restored.fromDateFilter ?? this.fromDateFilter;
        this.toDateFilter = restored.toDateFilter ?? this.toDateFilter;
        this.searchTerm = restored.searchTerm ?? '';
      }
    });

    // Subscribe to filter changes for UI sync
    this.currentFilter$.pipe(takeUntil(this.destroy$)).subscribe(filter => {
      if (filter.sort) {
        const isDescending = filter.sort.startsWith('-');
        this.currentSortField = isDescending
          ? filter.sort.substring(1)
          : filter.sort;
        this.currentSortDirection = isDescending ? 'desc' : 'asc';

        // Update Ignite table column sort state
        Object.keys(this.columnNames).forEach(key => {
          const colObj = this.columnNames[key];
          if (typeof colObj === 'object') {
            colObj.sorted =
              key === this.currentSortField
                ? (this.currentSortDirection as 'asc' | 'desc')
                : 'none';
          }
        });
      } else {
        this.currentSortField = null;
        this.currentSortDirection = null;

        // Reset all column sort states
        Object.keys(this.columnNames).forEach(key => {
          const colObj = this.columnNames[key];
          if (typeof colObj === 'object') {
            colObj.sorted = 'none';
          }
        });
      }
    });
    this.initializeSearch(term => this.facade.search(term));
    this.initializeColumnSearch([
      'requestNumber',
      'status',
      'requestDate',
      'arNumber',
      'country',
      'printSite',
      'createdBy',
      'assignedTo',
      'distributionCount',
      'documentCount',
      'isExpedited',
      'isPrinted',
      'shippingMethods',
      'requiresManualCheck',
    ]);

    this.columnLabelMap = {
      requestNumber: 'Request ID',
      status: 'Status',
      requestDate: 'Date',
      arNumber: 'AR No',
      country: 'Country',
      printSite: 'Print Site',
      createdBy: 'Created By',
      assignedTo: 'Assigned To',
      distributionCount: 'Distro Count',
      documentCount: 'Doc Count',
      isExpedited: 'Expedited?',
      isPrinted: 'Printed?',
      shippingMethods: 'Shipping',
      requiresManualCheck: 'Manual Check?',
    };

    this.requests$.pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (result !== null) {
        this.hasLoadedOnce = true;
      }
    });

    // Listen for signal to open create modal (from external auth redirect)
    this.createRequestModalService.openCreateModal$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.openCreateWizard();
      });

    // Load requests data
    this.facade.loadRequests();
  }

  onTableSort(sortDetail: { key: string; isAsc: boolean | 'none' }): void {
    // Handle sort from Ignite table component
    this.onSort(sortDetail.key);
  }

  onCheckboxChanged(request: Request): void {
    if (this.isRowSelected(request.id)) {
      this.selectedRows.delete(request.id);
      this.selectedRequests = this.selectedRequests.filter(
        r => r.id !== request.id
      );
    } else if (!this.isCheckboxDisabled(request)) {
      this.selectedRows.add(request.id);
      this.selectedRequests.push(request);
    }
  }

  toggleSelectAllRows(items: Request[]): void {
    if (this.selectAll) {
      this.selectedRows.clear();
      this.selectedRequests = [];
      this.selectAll = false;
    } else {
      this.selectAll = true;
      this.selectedRequests = [];
      items.forEach(item => {
        if (!this.isCheckboxDisabled(item)) {
          this.selectedRows.add(item.id);
          this.selectedRequests.push(item);
        }
      });
    }
  }

  getColumnValue(request: Request, field: string): string {
    if (field === 'status') {
      return request.statusDisplay || this.getStatusLabel(request.status);
    }
    if (field === 'requestDate') {
      return this.formatDate(request.requestDate);
    }
    if (field === 'distributionCount') {
      return request.distributionCount?.toString() ?? 'Pending';
    }
    if (field === 'isPrinted') {
      return this.formatPrintedValue(request.isPrinted);
    }
    if (field === 'isExpedited' || field === 'requiresManualCheck') {
      const value = (request as unknown as Record<string, unknown>)[field];
      return value ? 'Yes' : 'No';
    }
    const value = (request as unknown as Record<string, unknown>)[field];
    if (value === null || value === undefined) {
      return '-';
    }
    if (typeof value === 'object' || typeof value === 'function') {
      return '-';
    }
    return (value as string | number | boolean).toString();
  }

  handleTableClick(event: Event): void {
    const target = event.target as HTMLElement;

    // Find the closest table row
    const row = target.closest('tr');
    if (!row) {
      return;
    }

    // Skip header rows
    if (row.parentElement?.tagName === 'THEAD') {
      return;
    }

    // Get all cells in the row
    const cells = Array.from(row.querySelectorAll('td'));
    if (cells.length === 0) {
      return;
    }

    // The first cell should contain the request number (e.g., "REQ-002619")
    const firstCell = cells[1];
    const requestNumberText = firstCell?.textContent?.trim();

    if (requestNumberText) {
      // Extract just the number from "REQ-002619" format
      const match = /REQ-(\d+)/.exec(requestNumberText);
      if (match?.[1]) {
        const requestNumber = Number.parseInt(match[1], 10);
        this.router.navigate(['/requestDetails', requestNumber]);
      } else {
        // Try treating the whole text as a number
        const requestNumber = Number.parseInt(
          requestNumberText.replace(/\D/g, ''),
          10
        );
        if (!Number.isNaN(requestNumber)) {
          this.router.navigate(['/requestDetails', requestNumber]);
        }
      }
    }
  }

  override onPageChange(page: number): void {
    super.onPageChange(page);
    this.clearSelection();
  }

  override onPageSizeChange(pageSize: number): void {
    super.onPageSizeChange(pageSize);
    this.clearSelection();
  }

  onStatusFilterChange(value: string | null): void {
    this.statusFilter = value === '' ? null : value;
    this.applyFilters();
  }

  onFromDateFilterChange(value: string): void {
    this.fromDateFilter = value === '' ? null : value;
    this.applyFilters();
  }

  onToDateFilterChange(value: string): void {
    this.toDateFilter = value === '' ? null : value;
    this.applyFilters();
  }

  applyFilters(): void {
    const filters = {
      statusFilter: this.statusFilter,
      fromDateFilter: this.fromDateFilter,
      toDateFilter: this.toDateFilter,
      searchTerm: this.searchTerm,
    };

    this.filterManager.updateQueryParams(filters);
    this.filterManager.saveFilters(filters);

    const requestFilter: Record<string, unknown> = {
      status: this.statusFilter ?? undefined,
      fromDate: this.fromDateFilter ?? undefined,
      toDate: this.toDateFilter ?? undefined,
      search: this.searchTerm?.trim() ?? undefined,
    };

    // Helper to convert string boolean values (Yes/No/Y/N) to actual boolean
    const convertToBoolean = (value?: string): boolean | undefined => {
      if (!value?.trim()) return undefined;
      const normalized = value.trim().toLowerCase();
      if (normalized === 'yes' || normalized === 'y') return true;
      if (normalized === 'no' || normalized === 'n') return false;
      return undefined;
    };

    // Only include column filters that have values
    const addStringColumnFilter = (
      columnKey: string,
      requestKey?: string
    ): void => {
      if (this.columnFilters[columnKey]?.trim()) {
        requestFilter[requestKey ?? columnKey] = this.columnFilters[columnKey];
      }
    };

    addStringColumnFilter('status');
    addStringColumnFilter('requestNumber');
    addStringColumnFilter('requestDate');
    addStringColumnFilter('arNumber');
    addStringColumnFilter('createdBy');
    addStringColumnFilter('country');
    addStringColumnFilter('printSite');
    addStringColumnFilter('assignedTo', 'assignedUserName');
    addStringColumnFilter('distributionCount');
    addStringColumnFilter('documentCount');

    // Boolean filters - convert Yes/No/Y/N to true/false
    const isPrintedValue = convertToBoolean(this.columnFilters['isPrinted']);
    if (isPrintedValue !== undefined) {
      requestFilter['isPrinted'] = isPrintedValue;
    }

    const isExpeditedValue = convertToBoolean(
      this.columnFilters['isExpedited']
    );
    if (isExpeditedValue !== undefined) {
      requestFilter['isExpedited'] = isExpeditedValue;
    }

    const requiresManualCheckValue = convertToBoolean(
      this.columnFilters['requiresManualCheck']
    );
    if (requiresManualCheckValue !== undefined) {
      requestFilter['isManualCheck'] = requiresManualCheckValue;
    }

    if (this.columnFilters['shippingMethods']?.trim()) {
      requestFilter['shippingMethods'] = this.columnFilters['shippingMethods'];
    }

    this.facade.applyFilters(requestFilter as Partial<RequestFilter>);
  }

  clearAllFilters(): void {
    this.statusFilter = null;
    this.fromDateFilter = null;
    this.toDateFilter = null;
    this.searchTerm = '';
    this.columnFilters = resetColumnFilters(this.columnFilters);
    this.columnFiltersSubject.next({ ...this.columnFilters });
    this.facade.clearFilters();

    const clearedFilters = {
      statusFilter: this.statusFilter,
      fromDateFilter: this.fromDateFilter,
      toDateFilter: this.toDateFilter,
      searchTerm: this.searchTerm,
    };

    this.filterManager.clearFilters(clearedFilters);
  }

  override clearColumnFilter(columnKey: string): void {
    super.clearColumnFilter(columnKey);
    this.applyFilters();
  }

  override onColumnSearch(columnKey: string, value: string): void {
    super.onColumnSearch(columnKey, value);
    this.applyFilters();
  }

  get activeFilterCount(): number {
    return getActiveFilterCount({
      statusFilter: this.statusFilter,
      fromDateFilter: this.fromDateFilter,
      toDateFilter: this.toDateFilter,
    });
  }

  get hasActiveFilters(): boolean {
    return hasActiveFilters(
      {
        statusFilter: this.statusFilter,
        fromDateFilter: this.fromDateFilter,
        toDateFilter: this.toDateFilter,
      },
      this.searchTerm
    );
  }

  isRowSelected(id: string): boolean {
    return this.selectedRows.has(id);
  }

  toggleRowSelection(id: string): void {
    if (this.selectedRows.has(id)) {
      this.selectedRows.delete(id);
    } else {
      this.selectedRows.add(id);
    }
  }

  toggleSelectAll(items: Request[]): void {
    if (this.selectAll) {
      this.selectedRows.clear();
      this.selectAll = false;
    } else {
      items.forEach(item => this.selectedRows.add(item.id));
      this.selectAll = true;
    }
  }

  toggleSelectAllSlotted(items: Request[], event: CustomEvent): void {
    const isChecked = event.detail?.checked ?? false;
    if (isChecked) {
      this.selectAll = true;
      this.selectedRequests = [];
      items.forEach(item => {
        if (!this.isCheckboxDisabled(item)) {
          this.selectedRows.add(item.id);
          this.selectedRequests.push(item);
        }
      });
    } else {
      this.selectAll = false;
      this.selectedRows.clear();
      this.selectedRequests = [];
    }
  }

  isCheckboxDisabled(request: Request): boolean {
    // Disable checkboxes for non-Open status OR already printed requests
    return request.statusDisplay !== 'Open' || request.isPrinted === true;
  }

  onCheckboxChange(request: Request, event: CustomEvent): void {
    const isChecked = event.detail?.checked ?? false;
    if (isChecked) {
      this.selectedRows.add(request.id);
      if (!this.selectedRequests.some(r => r.id === request.id)) {
        this.selectedRequests.push(request);
      }
    } else {
      this.selectedRows.delete(request.id);
      this.selectedRequests = this.selectedRequests.filter(
        r => r.id !== request.id
      );
    }
  }

  private clearSelection(): void {
    this.selectedRows.clear();
    this.selectAll = false;
  }

  formatBooleanYes(): string {
    return 'Yes';
  }

  formatBooleanNo(): string {
    return 'No';
  }

  getStatusClass(status: RequestStatus): string {
    switch (status) {
      case RequestStatus.Pending:
        return 'status-pending';
      case RequestStatus.InProgress:
        return 'status-in-progress';
      case RequestStatus.Completed:
        return 'status-completed';
      case RequestStatus.Cancelled:
        return 'status-cancelled';
      case RequestStatus.OnHold:
        return 'status-on-hold';
      case RequestStatus.Error:
        return 'status-error';
      default:
        return '';
    }
  }

  getStatusLabel(status: RequestStatus | string | number): string {
    // Handle both enum values and string/number from API
    if (typeof status === 'string') {
      return status;
    }
    const label = RequestStatusLabels[status as RequestStatus];
    return label || String(status) || 'Unknown';
  }

  getStatusDisplay(request: Request): string {
    // Try statusDisplay first, fall back to enum label
    const display = request.statusDisplay?.trim();
    return display || this.getStatusLabel(request.status);
  }

  getStatusClassFromString(status: string): string {
    if (!status) return '';
    return this.statusColorMap[status] || 'status-in-progress';
  }

  // Map status to ukg-badge variant
  getStatusVariant(status: RequestStatus): string {
    switch (status) {
      case RequestStatus.Completed:
        return 'success';
      case RequestStatus.InProgress:
      case RequestStatus.Pending:
        return 'info';
      case RequestStatus.Cancelled:
        return 'warning';
      case RequestStatus.Error:
        return 'error';
      default:
        return 'neutral';
    }
  }

  getSortIcon(field: string): string {
    if (this.currentSortField !== field) return '';
    return this.currentSortDirection === 'asc' ? '↑' : '↓';
  }

  getSortDirection(field: string): string {
    if (this.currentSortField !== field) {
      return '';
    }
    return this.currentSortDirection ?? '';
  }

  getDateCellValue(row: Request, field: string): string {
    const value = this.getCellValue(row, field);
    return typeof value === 'string' ? value : '';
  }

  getBooleanCellValue(row: Request, field: string): boolean {
    const value = this.getCellValue(row, field);
    return typeof value === 'boolean' ? value : false;
  }

  get selectedCount(): number {
    return this.selectedRows.size;
  }

  private formatPrintedValue(isPrinted: boolean | null): string {
    if (isPrinted === null || isPrinted === undefined) {
      return '-';
    }
    return isPrinted ? 'Yes' : 'No';
  }

  protected override formatDate(dateString: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return 'Invalid Date';
    const pad = (value: number): string => value.toString().padStart(2, '0');
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const year = date.getFullYear();
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    return `${month}/${day}/${year} ${hours}:${minutes}`;
  }

  onRowClick(request: Request, event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.closest('.checkbox-column')) {
      return;
    }
    this.navigateToRequestDetails(request);
  }

  onRowKeydown(request: Request): void {
    this.navigateToRequestDetails(request);
  }

  private navigateToRequestDetails(request: Request): void {
    this.router.navigate(['/requestDetails', request.requestNumber]);
  }

  openCreateWizard(): void {
    this.showCreateWizard = true;
  }

  closeCreateWizard(): void {
    this.showCreateWizard = false;
    // Refresh the requests list after closing the wizard
    this.facade.refresh();
  }

  openBulkUpdateModal(): void {
    if (this.selectedRequests.length > 0) {
      this.showBulkUpdateModal = true;
    }
  }

  closeBulkUpdateModal(): void {
    this.showBulkUpdateModal = false;
    this.clearSelection();
  }

  getSelectedRequestObjects(): Request[] {
    return this.selectedRequests;
  }
}
